import { Plus } from "lucide-react"
import { ReactComponent as MaterialsEmptyState } from "../../../../assets/svg/materials-empty-state.svg"
import CustomSelect from "../../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import type { MaterialsAutofillAnalysisContext } from "../../../../services/materialsQuotationAutofillDraftService.ts"
import type { CategorySelectOption } from "./CalculadoraCatalogToolbar.tsx"
import styles from "../CalculadoraDeMateriales.module.scss"

export type AutofillImportSummary = {
  lineCount: number
  categoryCount: number
  fromCatalogCount: number
  customOrUnmatchedCount: number
}

export type BrowseCategoryItem = {
  id: string
  name: string
  count: number
}

export type CalculadoraMaterialsEmptyPanelProps = {
  showSecondEmptyState: boolean
  showCategoryBrowseSuggestions: boolean
  compactMaterialsCenter: boolean
  autofillImportSummary: AutofillImportSummary | null
  analysisContext: MaterialsAutofillAnalysisContext | null
  titleWhenEmpty: string
  subTitleWhenEmpty: string
  categoryOptions: CategorySelectOption[]
  selectedCategory: string
  toggleCategory: (categoryId: string) => void
  handleSearchLinkClick: () => void
  setShowAddCustomPopup: (v: boolean) => void
  topCategoriesForBrowse: BrowseCategoryItem[]
  onSelectBrowseCategory: (categoryId: string) => void
  totalCatalogMaterialsLabel: string
  isEstimacionDeCostos?: boolean
  estimationCatalogGapCount?: number
  onRequestQuotesFromEstimation?: () => void
}

const relevanceBannerText = (ctx: MaterialsAutofillAnalysisContext): string | null => {
  const dr = ctx.documentRelevance
  if (dr === "unlikely_materials_list") {
    return "El archivo no encaja del todo con un listado típico de materiales; revisá el resumen con detención."
  }
  if (dr === "other_or_unclear") {
    return "No quedó claro si el documento es un listado o presupuesto de materiales; conviene validar cada línea en el resumen."
  }
  return null
}

const CalculadoraMaterialsEmptyPanel = ({
  showSecondEmptyState,
  showCategoryBrowseSuggestions,
  compactMaterialsCenter,
  autofillImportSummary,
  analysisContext,
  titleWhenEmpty,
  subTitleWhenEmpty,
  categoryOptions,
  selectedCategory,
  toggleCategory,
  handleSearchLinkClick,
  setShowAddCustomPopup,
  topCategoriesForBrowse,
  onSelectBrowseCategory,
  totalCatalogMaterialsLabel,
  isEstimacionDeCostos = false,
  estimationCatalogGapCount = 0,
  onRequestQuotesFromEstimation,
}: CalculadoraMaterialsEmptyPanelProps) => {
  const formatMaterialCount = (count: number) =>
    count === 1 ? "1 material" : `${new Intl.NumberFormat("es-AR").format(count)} materiales`
  const relevanceText =
    compactMaterialsCenter && analysisContext ? relevanceBannerText(analysisContext) : null
  const analysisNarrative =
    Boolean(analysisContext?.summary) || Boolean(analysisContext?.notesForUser)
  const showAnalysisCard =
    compactMaterialsCenter &&
    (Boolean(relevanceText) || analysisNarrative || autofillImportSummary != null)
  const hideOuterTitleWhenCompactAnalysis = compactMaterialsCenter && showAnalysisCard

  return (
    <div
      className={`${styles.container_body_categoryMaterials_empty} ${
        compactMaterialsCenter ? styles.container_body_categoryMaterials_emptyCompactBelowToolbar : ""
      }`}
    >
      {!showSecondEmptyState && !compactMaterialsCenter && (
        <div className={styles.container_body_categoryMaterials_empty_visual}>
          <MaterialsEmptyState className={styles.container_body_categoryMaterials_empty_icon} aria-hidden />
        </div>
      )}
      <div
        className={`${styles.container_body_categoryMaterials_empty_content} ${
          showSecondEmptyState || compactMaterialsCenter
            ? styles.container_body_categoryMaterials_empty_content_compact
            : ""
        }`}
      >
        {!hideOuterTitleWhenCompactAnalysis ? (
          <>
            <h2 className={styles.container_body_categoryMaterials_empty_title}>{titleWhenEmpty}</h2>
            <p className={styles.container_body_categoryMaterials_empty_text}>{subTitleWhenEmpty}</p>
          </>
        ) : null}
        {showAnalysisCard ? (
          <div className={styles.container_body_categoryMaterials_empty_importSummary}>
            {compactMaterialsCenter ? (
              <span className={styles.container_body_categoryMaterials_empty_importSummaryIaEyebrow}>
                Análisis automático de tu archivo
              </span>
            ) : null}
            {relevanceText ? (
              <div
                className={`${styles.container_body_categoryMaterials_empty_relevanceBanner} ${
                  analysisContext?.documentRelevance === "unlikely_materials_list"
                    ? styles.container_body_categoryMaterials_empty_relevanceBanner_warn
                    : styles.container_body_categoryMaterials_empty_relevanceBanner_muted
                }`}
                role="status"
              >
                {relevanceText}
              </div>
            ) : null}
            {analysisNarrative ? (
              <>
                <div className={styles.container_body_categoryMaterials_empty_importSummaryAnalysis}>
                  {analysisContext?.summary ? (
                    <p className={styles.container_body_categoryMaterials_empty_importSummaryAnalysisLead}>
                      {analysisContext.summary}
                    </p>
                  ) : null}
                  {analysisContext?.notesForUser ? (
                    <p className={styles.container_body_categoryMaterials_empty_importSummaryAnalysisNote}>
                      {analysisContext.notesForUser}
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
            {autofillImportSummary ? (
              <>
                <h3
                  className={`${styles.container_body_categoryMaterials_empty_importSummaryTitle} ${
                    analysisNarrative || relevanceText
                      ? styles.container_body_categoryMaterials_empty_importSummaryTitleSecondary
                      : ""
                  }`}
                >
                  {analysisNarrative || relevanceText ? "Detalle del listado" : "Lo que detectamos en el archivo"}
                </h3>
                <ul className={styles.container_body_categoryMaterials_empty_importSummaryList}>
                  <li>
                    {autofillImportSummary.lineCount}{" "}
                    {autofillImportSummary.lineCount === 1 ? "línea cargada" : "líneas cargadas"} en tu listado de
                    trabajo.
                  </li>
                  <li>
                    {autofillImportSummary.categoryCount}{" "}
                    {autofillImportSummary.categoryCount === 1
                      ? "categoría distinta"
                      : "categorías distintas"}{" "}
                    en esas líneas.
                  </li>
                  {autofillImportSummary.fromCatalogCount > 0 ? (
                    <li>
                      {autofillImportSummary.fromCatalogCount}{" "}
                      {autofillImportSummary.fromCatalogCount === 1
                        ? "material enlazado al catálogo de Edify"
                        : "materiales enlazados al catálogo de Edify"}
                      .
                    </li>
                  ) : null}
                  {autofillImportSummary.customOrUnmatchedCount > 0 ? (
                    <li>
                      {autofillImportSummary.customOrUnmatchedCount}{" "}
                      {isEstimacionDeCostos
                        ? autofillImportSummary.customOrUnmatchedCount === 1
                          ? "ítem del archivo sin precio de referencia en el catálogo Edify"
                          : "ítems del archivo sin precio de referencia en el catálogo Edify"
                        : autofillImportSummary.customOrUnmatchedCount === 1
                          ? "ítem como material personalizado o no reconocido en catálogo"
                          : "ítems como materiales personalizados o no reconocidos en catálogo"}
                      {isEstimacionDeCostos
                        ? " — revisalos en el resumen."
                        : "; revisalos en el "}
                      {!isEstimacionDeCostos ? (
                        <>
                          <strong>resumen a la derecha</strong>.
                        </>
                      ) : null}
                    </li>
                  ) : null}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
        {isEstimacionDeCostos &&
        estimationCatalogGapCount > 0 &&
        onRequestQuotesFromEstimation ? (
          <div
            className={styles.container_body_categoryMaterials_empty_estimationPublishBanner}
            role="region"
            aria-label="Publicar para obtener precios"
          >
            <p className={styles.container_body_categoryMaterials_empty_estimationPublishBannerText}>
              Hay materiales que vimos en tu archivo pero no tienen referencia de precio en nuestro catálogo. Podés{" "}
              <strong>pedir cotización a proveedores en Edify</strong>: la red te ayuda a conseguir precios reales.
            </p>
            <button
              type="button"
              className={styles.container_body_categoryMaterials_empty_estimationPublishBannerBtn}
              onClick={onRequestQuotesFromEstimation}
            >
              Ir a pedir cotización con esta lista
            </button>
          </div>
        ) : null}
        {compactMaterialsCenter ? (
          <p className={styles.container_body_categoryMaterials_empty_nextStepsCompact} role="note">
            Para sumar más materiales usá la barra superior (categoría o búsqueda por nombre). Si no está en el
            catálogo, podés crear un material personalizado.
          </p>
        ) : null}
        {compactMaterialsCenter ? (
          <button
            type="button"
            className={`${styles.container_body_categoryMaterials_empty_searchBtn} ${styles.customMaterialEmptyBtn} ${styles.container_body_categoryMaterials_empty_compactCta}`}
            onClick={() => setShowAddCustomPopup(true)}
          >
            <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
            Crear material personalizado
          </button>
        ) : null}
        {showCategoryBrowseSuggestions ? (
          <div className={styles.container_body_categoryMaterials_empty_categoryBrowse}>
            <h3 className={styles.container_body_categoryMaterials_empty_categoryBrowse_title}>
              Explorar por rubro
            </h3>
            <p className={styles.container_body_categoryMaterials_empty_categoryBrowse_intro}>
              El catálogo tiene {totalCatalogMaterialsLabel} materiales organizados por rubro. Elegí uno para ver
              su listado y sumar ítems a tu {isEstimacionDeCostos ? "estimación" : "cotización"}.
            </p>
            <div className={styles.container_body_categoryMaterials_empty_categoryBrowse_grid}>
              {topCategoriesForBrowse.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={styles.container_body_categoryMaterials_empty_categoryBrowse_card}
                  onClick={() => onSelectBrowseCategory(category.id)}
                >
                  <span className={styles.container_body_categoryMaterials_empty_categoryBrowse_cardName}>
                    {category.name}
                  </span>
                  <span className={styles.container_body_categoryMaterials_empty_categoryBrowse_cardCount}>
                    {formatMaterialCount(category.count)}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={styles.container_body_categoryMaterials_empty_searchLink}
              onClick={handleSearchLinkClick}
            >
              ¿Sabés qué necesitás? Buscar por nombre
            </button>
          </div>
        ) : null}
        {!showSecondEmptyState && !compactMaterialsCenter ? (
          <>
            <div className={styles.container_body_categoryMaterials_empty_dropdown}>
              <CustomSelect
                value={selectedCategory}
                onChange={toggleCategory}
                options={categoryOptions}
                placeholder="Elegí una categoría para empezar"
                placement="top"
                searchable
                searchPlaceholder="Buscar categoría..."
              />
            </div>
            <div className={styles.container_body_categoryMaterials_empty_actions}>
              <button
                type="button"
                className={styles.container_body_categoryMaterials_empty_searchLink}
                onClick={handleSearchLinkClick}
              >
                ¿Sabés qué necesitás? Buscar por nombre
              </button>
              <button
                type="button"
                className={`${styles.container_body_categoryMaterials_empty_searchBtn} ${styles.customMaterialEmptyBtn}`}
                onClick={() => setShowAddCustomPopup(true)}
              >
                <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
                Crear material personalizado
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default CalculadoraMaterialsEmptyPanel
